'use client';

import type { ReactNode} from 'react';
import { createContext, useContext, useRef } from 'react';
import { createStore, useStore } from 'zustand';

import { mapModule } from '../helpers/advertising/mapAdModule';
import type { AdvertisingMap, AdvertisingZone } from '../types/modules';

type AdvertisingProps = {
  mappedModules: AdvertisingMap;
};

export type AdvertisingState = {
  setMappedModules(newMapped: AdvertisingMap): void;
} & AdvertisingProps;

export const createAdvertisingStore = (initialZones: AdvertisingZone[] = []) => {
  return createStore<AdvertisingState>((set) => ({
    mappedModules: mapModule(initialZones),
    setMappedModules: (newMapped: AdvertisingState['mappedModules']) => {
      return set(() => ({
        mappedModules: newMapped,
      }));
    },
  }));
};

type AdvertisingStore = ReturnType<typeof createAdvertisingStore>;

const AdvertisingContext = createContext<AdvertisingStore | null>(null);

type AdvertisingProviderProps = {
  mappedModules?: AdvertisingZone[];
  children: ReactNode;
};

export const AdvertisingProvider = ({ children, ...props }: AdvertisingProviderProps) => {
  const storeRef = useRef<AdvertisingStore>(null);
  if (!storeRef.current) {
    storeRef.current = createAdvertisingStore(props.mappedModules);
  }
  return <AdvertisingContext.Provider value={storeRef.current}>{children}</AdvertisingContext.Provider>;
};

export function useAdvertisingStore<T>(
  selector: (state: AdvertisingState) => T,
  equalityFn?: (left: T, right: T) => boolean,
): T {
  const store = useContext(AdvertisingContext);
  if (!store) throw new Error('Missing AdvertisingContext.Provider in the tree');
  return useStore(store, selector, equalityFn);
}
